import { observer } from "mobx-react-lite";
import React, { useState } from "react";
import { FormattedMessage } from "react-intl";
import { Linking, ScrollView } from "react-native";
import { Spacer } from "~/components/Spacer";
import { BodyText } from "~/components/V2/Text/BodyText";
import { View } from "~/components/V2/View";
import { environment } from "~/environment";
import { styleConstantsV2 } from "~/style/styleConstantsV2";
import { getPhoneNumberWithCountryCode } from "~/utils/getPhoneNumberWithCountryCode";
import { Button } from "../Button";
import { Icon } from "../Icon";
import { BrandText } from "./Text/BrandText";

// TODO: Move to environment
const supportEmail = "kundservice@evidensia.se";

export const ErrorViewV2 = observer(function ErrorView({
  error,
  title,
  body,
  showAdditionalHelp,
}: {
  error: unknown;
  title?: string;
  body?: string | React.ReactNode | React.ReactNode[];
  showAdditionalHelp?: boolean;
}) {
  const [isErrorVisible, setIsErrorVisible] = useState(false);

  return (
    <View padding24>
      <View centerContent>
        <Icon
          name="error-warning"
          size={144}
          color={styleConstantsV2.colorError}
        />
      </View>
      <Spacer large />

      <BrandText alignCenter sizeH1 weightExtraBold colorBlack>
        {title ??
          intl.formatMessage({
            id: "HYP1NC",
            defaultMessage: "Something went wrong",
            description: "Error view title",
          })}
      </BrandText>

      <Spacer large />

      {body === undefined ? (
        <BodyText sizeExtraLarge alignCenter colorBlack>
          {intl.formatMessage({
            id: "J2tn32",
            defaultMessage:
              "Sorry something went wrong, please try again later",
            description: "Subtitle in error view",
          })}
        </BodyText>
      ) : typeof body === "string" ? (
        <BodyText alignCenter sizeExtraLarge colorBlack>
          {body}
        </BodyText>
      ) : (
        body
      )}

      <Spacer extraLarge />

      {!!showAdditionalHelp && (
        <View centerContent>
          <BodyText sizeExtraLarge weightBold>
            <FormattedMessage
              id="MbYUIO"
              defaultMessage="To resolve this, you can:"
              description="Error view additional help title"
            />
          </BodyText>
          <BodyText sizeExtraLarge weightRegular alignCenter>
            <FormattedMessage
              id="vtnjuG"
              defaultMessage="Call us at <link>{phoneNumber}</link> for quick assistance."
              description="Error view additional help body"
              values={{
                link: (chunk) => (
                  <BodyText
                    sizeExtraLarge
                    colorPrimary
                    underline
                    onPress={() => {
                      Linking.openURL(
                        `tel:${getPhoneNumberWithCountryCode(environment.SUPPORT_PHONE_NUMBER_COUNTRY_CODE, environment.SUPPORT_PHONE_NUMBER)}`,
                      );
                    }}
                  >
                    {chunk}
                  </BodyText>
                ),
                phoneNumber: environment.SUPPORT_PHONE_NUMBER,
              }}
            />
          </BodyText>
          <Spacer extraExtraLarge />

          <BodyText sizeExtraLarge weightRegular alignCenter>
            <FormattedMessage
              id="VkqHcN"
              defaultMessage="Email us at <link>{supportEmail}</link>, and we will get back to you as soon as possible."
              description="Error view additional help body"
              values={{
                link: (chunk) => (
                  <BodyText
                    sizeExtraLarge
                    colorPrimary
                    underline
                    onPress={() => {
                      Linking.openURL(`mailto:${supportEmail}`);
                    }}
                  >
                    {chunk}
                  </BodyText>
                ),
                supportEmail: supportEmail,
              }}
            />
          </BodyText>
        </View>
      )}

      {!!body && <Spacer small />}

      {__DEV__ && (
        <>
          <Button
            small
            transparent
            onPress={() => setIsErrorVisible((n) => !n)}
          >
            <BodyText sizeSmall colorBlack weightRegular>
              {isErrorVisible
                ? intl.formatMessage({
                    defaultMessage: "Hide details",
                    id: "k+3605",
                    description: "Error view button to hide details of error",
                  })
                : intl.formatMessage({
                    defaultMessage: "Show details",
                    description:
                      "Error view button to show details of error. Shown on ErrorView",
                    id: "8faccV",
                  })}
            </BodyText>
          </Button>

          <Spacer />
          {isErrorVisible && (
            <View
              alignSelfStretch
              borderRadius24
              style={{ overflow: "hidden" }}
            >
              <ScrollView style={{ maxHeight: 300 }} bounces={false}>
                <ScrollView
                  horizontal
                  contentContainerStyle={{
                    backgroundColor: styleConstantsV2.colorPrimaryMid,
                    minWidth: "100%",
                    padding: styleConstantsV2.spacing16,
                  }}
                  bounces={false}
                  showsHorizontalScrollIndicator={false}
                  showsVerticalScrollIndicator={false}
                >
                  <BodyText>{String(error)}</BodyText>
                </ScrollView>
              </ScrollView>
            </View>
          )}
        </>
      )}
    </View>
  );
});
